(() => {
  'use strict';

  const BODY_OPEN_CLASS = 'frk-substrate-sheet-open';
  const HALF_KG = 0.5;

  function numberValue(value) {
    const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.]+/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function integerValue(value) {
    const parsed = Number.parseInt(String(value ?? '').replace(/[^0-9-]+/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatKg(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  }

  function booleanValue(value) {
    return String(value).toLowerCase() === 'true';
  }

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function matchesG4Damp(title) {
    const text = normalise(title);
    return text.includes('g4') && text.includes('damp') && text.includes('seal') && !text.includes('pond sealer');
  }

  function parseG4PackSize(title, handle) {
    const source = `${title || ''} ${handle || ''}`.replace(/-/g, ' ');

    // Check 2.5kg before 5kg. Handles such as "g4-damp-sealer-2-5kg"
    // become "2 5kg" after normalisation and would otherwise be mistaken
    // for a 5kg product.
    if (/(?:^|[^0-9])25\s*kg\b/i.test(source)) return 25;
    if (/(?:^|[^0-9])2(?:[.\s]?5)\s*kg\b/i.test(source)) return 2.5;
    if (/(?:^|[^0-9])5\s*kg\b/i.test(source)) return 5;
    if (/(?:^|[^0-9])1\s*kg\b/i.test(source)) return 1;
    return 0;
  }

  function packOptions(root) {
    return Array.isArray(root.__frkG4PackOptions)
      ? root.__frkG4PackOptions.filter((pack) => pack.priceCents > 0 && pack.variantId > 0 && pack.available)
      : [];
  }

  function embeddedG4PackOptions(root) {
    const node = root.querySelector('[data-frk-g4-pack-data]');
    if (!node) return [];

    try {
      const parsed = JSON.parse(node.textContent || '[]');
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((pack) => ({
          size: numberValue(pack.size),
          handle: String(pack.handle || ''),
          url: String(pack.url || ''),
          priceCents: integerValue(pack.priceCents),
          variantId: integerValue(pack.variantId),
          available: Boolean(pack.available)
        }))
        .filter((pack) => pack.size > 0 && pack.priceCents > 0 && pack.variantId > 0);
    } catch (error) {
      console.warn('[FRK G4] Could not parse embedded pack data.', error);
      return [];
    }
  }

  function shopRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function productJsonUrl(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = parsed.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    return parsed.href;
  }

  async function fetchPackProduct(definition) {
    const response = await fetch(productJsonUrl(definition.url), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Unable to load ${definition.url}`);
    }

    const product = await response.json();
    if (!matchesG4Damp(product.title)) {
      throw new Error(`Unexpected product returned for ${definition.url}`);
    }

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((item) => item.available) || variants[0];
    if (!variant) throw new Error(`No variant found for ${definition.url}`);

    const size = parseG4PackSize(product.title, product.handle);
    if (!size) throw new Error(`Unable to determine pack size for ${definition.url}`);

    return {
      size,
      handle: product.handle,
      url: product.url || definition.url,
      priceCents: integerValue(variant.price ?? product.price),
      variantId: integerValue(variant.id),
      available: Boolean(variant.available)
    };
  }

  async function predictiveG4Urls() {
    const params = new URLSearchParams();
    params.set('q', 'G4 Damp Sealer');
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][unavailable_products]', 'show');

    try {
      const response = await fetch(`${shopRoot()}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return [];
      const data = await response.json();
      return (data?.resources?.results?.products || [])
        .filter((product) => matchesG4Damp(product.title))
        .map((product) => product.url)
        .filter(Boolean);
    } catch (_) {
      return [];
    }
  }

  function fallbackG4Urls(root) {
    const handles = [
      root.dataset.frkG4Handle1,
      root.dataset.frkG4Handle25kg,
      root.dataset.frkG4Handle5,
      root.dataset.frkG4Handle25,
      'g4-damp-sealer-1kg',
      'g4-damp-sealer-1kg-1',
      'g4-damp-sealer-2-5kg',
      'g4-damp-sealer-2-5kg-1',
      'g4-damp-sealer-5kg',
      'g4-damp-sealer-5kg-1',
      'g4-damp-sealer-25kg',
      'g4-damp-sealer-25kg-1'
    ].filter(Boolean);

    return [...new Set(handles)].map((handle) => `${shopRoot()}products/${handle}`);
  }

  async function discoverG4PackOptions(root) {
    const predictive = await predictiveG4Urls();
    const urls = [...new Set([...fallbackG4Urls(root), ...predictive])];
    const results = await Promise.allSettled(urls.map((url) => fetchPackProduct({ url })));
    const bySize = new Map();

    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      const pack = result.value;
      const existing = bySize.get(pack.size);
      if (!existing || (pack.available && !existing.available)) bySize.set(pack.size, pack);
    });

    return [...bySize.values()].sort((a, b) => b.size - a.size);
  }

  function setG4LoadingState(root) {
    const requiredTarget = root.querySelector('[data-frk-g4-required]');
    const packsTarget = root.querySelector('[data-frk-g4-packs]');
    const overageTarget = root.querySelector('[data-frk-g4-overage]');
    const totalTarget = root.querySelector('[data-frk-g4-total]');
    const button = root.querySelector('[data-frk-g4-add]');
    const size = numberValue(root.dataset.frkRoofSize);
    const required = Math.round(size * 0.5 * 10) / 10;

    if (requiredTarget) requiredTarget.textContent = formatKg(required);
    if (packsTarget) packsTarget.textContent = 'Loading pack options…';
    if (overageTarget) overageTarget.hidden = true;
    if (totalTarget) totalTarget.textContent = 'Loading…';
    if (button) {
      button.disabled = true;
      button.textContent = 'Loading G4 packs…';
    }
  }

  function mergeG4PackOptions(embedded, discovered) {
    const bySize = new Map();

    [...embedded, ...discovered].forEach((pack) => {
      if (!pack || !pack.size || !pack.priceCents || !pack.variantId) return;
      const existing = bySize.get(pack.size);

      if (!existing) {
        bySize.set(pack.size, pack);
        return;
      }

      // Prefer a purchasable product. If both are purchasable, retain the
      // Liquid-resolved product unless the discovered product is the only one
      // using the canonical handle or has a lower current price.
      if (pack.available && !existing.available) {
        bySize.set(pack.size, pack);
        return;
      }
      if (!pack.available || !existing.available) return;

      const canonicalHandles = {
        1: 'g4-damp-sealer-1kg',
        2.5: 'g4-damp-sealer-2-5kg',
        5: 'g4-damp-sealer-5kg',
        25: 'g4-damp-sealer-25kg'
      };
      const canonical = canonicalHandles[pack.size];
      const packCanonical = pack.handle === canonical;
      const existingCanonical = existing.handle === canonical;

      if (packCanonical && !existingCanonical) {
        bySize.set(pack.size, pack);
      } else if (packCanonical === existingCanonical && pack.priceCents < existing.priceCents) {
        bySize.set(pack.size, pack);
      }
    });

    return [...bySize.values()].sort((a, b) => b.size - a.size);
  }

  async function loadG4PackOptions(root) {
    if (root.__frkG4PackPromise) return root.__frkG4PackPromise;

    const embedded = embeddedG4PackOptions(root);
    const expectedSizes = [1, 2.5, 5, 25];
    const validEmbeddedSizes = new Set(
      embedded
        .filter((pack) => pack.available && pack.priceCents > 0 && pack.variantId > 0)
        .map((pack) => pack.size)
    );
    const embeddedComplete = expectedSizes.every((size) => validEmbeddedSizes.has(size));

    // Use the fast Liquid data only when all four purchasable pack sizes were
    // resolved. Shopify can cap all_products lookups on complex PDPs; treating
    // one successful lookup as a complete set caused 2.5kg to be omitted and
    // incorrectly recommended 3 x 1kg.
    if (embeddedComplete) {
      root.__frkG4PackOptions = embedded;
      updateG4(root);
      root.__frkG4PackPromise = Promise.resolve(embedded);
      return root.__frkG4PackPromise;
    }

    setG4LoadingState(root);
    root.__frkG4PackPromise = discoverG4PackOptions(root)
      .then((discovered) => {
        const merged = mergeG4PackOptions(embedded, discovered);
        root.__frkG4PackOptions = merged;
        updateG4(root);
        return merged;
      })
      .catch((error) => {
        console.warn('[FRK G4] Pack discovery failed.', error);
        root.__frkG4PackOptions = embedded;
        updateG4(root);
        return embedded;
      });

    return root.__frkG4PackPromise;
  }

  function isBetterState(candidate, current) {
    if (!current) return true;
    if (candidate.cost !== current.cost) return candidate.cost < current.cost;
    return candidate.packCount < current.packCount;
  }

  function cheapestPackBreakdown(requiredKg, packs) {
    if (!requiredKg || !packs.length) return null;

    const prepared = packs.map((pack, index) => ({
      ...pack,
      index,
      units: Math.round(pack.size / HALF_KG)
    }));

    const targetUnits = Math.ceil((requiredKg / HALF_KG) - 0.000001);
    const largestPackUnits = Math.max(...prepared.map((pack) => pack.units));
    const maxUnits = targetUnits + largestPackUnits;
    const dp = Array(maxUnits + 1).fill(null);
    dp[0] = { cost: 0, packCount: 0, counts: Array(prepared.length).fill(0) };

    for (let units = 1; units <= maxUnits; units += 1) {
      prepared.forEach((pack) => {
        const previousUnits = units - pack.units;
        if (previousUnits < 0 || !dp[previousUnits]) return;

        const previous = dp[previousUnits];
        const counts = previous.counts.slice();
        counts[pack.index] += 1;
        const candidate = {
          cost: previous.cost + pack.priceCents,
          packCount: previous.packCount + 1,
          counts
        };

        if (isBetterState(candidate, dp[units])) dp[units] = candidate;
      });
    }

    let best = null;
    for (let units = targetUnits; units <= maxUnits; units += 1) {
      const state = dp[units];
      if (!state) continue;

      const candidate = {
        ...state,
        units,
        overageUnits: units - targetUnits
      };

      if (
        !best
        || candidate.cost < best.cost
        || (candidate.cost === best.cost && candidate.overageUnits < best.overageUnits)
        || (candidate.cost === best.cost && candidate.overageUnits === best.overageUnits && candidate.packCount < best.packCount)
      ) {
        best = candidate;
      }
    }

    if (!best) return null;

    return {
      totalCents: best.cost,
      suppliedKg: best.units * HALF_KG,
      overageKg: best.overageUnits * HALF_KG,
      packs: prepared
        .map((pack) => ({
          size: pack.size,
          count: best.counts[pack.index],
          variantId: pack.variantId,
          priceCents: pack.priceCents
        }))
        .filter((pack) => pack.count > 0)
        .sort((a, b) => b.size - a.size)
    };
  }

  function packText(packs) {
    if (!packs.length) return 'Contact us for pack sizes';
    return packs.map(({ size, count }) => `${count} × ${formatKg(size)}kg`).join(' + ');
  }

  function fallbackMoney(cents) {
    const mode = window.__VAT && typeof window.__VAT.getMode === 'function'
      ? window.__VAT.getMode()
      : document.documentElement.getAttribute('data-vat-mode') || 'ex';
    const value = mode === 'inc' || mode === 'both' ? Math.round(cents * 1.2) : cents;
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value / 100);
  }

  function setG4Message(root, message, isError = false) {
    const target = root.querySelector('[data-frk-g4-message]');
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
    target.classList.toggle('is-error', Boolean(isError));
  }

  function setG4ButtonState(root, result) {
    const button = root.querySelector('[data-frk-g4-add]');
    if (!button) return;

    root.__frkG4Result = result || null;
    button.disabled = !result || !result.packs?.length;
    button.textContent = result
      ? `Add G4 packs to cart · ${fallbackMoney(result.totalCents)}`
      : 'G4 packs unavailable';
  }

  async function addG4Packs(root) {
    const button = root.querySelector('[data-frk-g4-add]');
    const result = root.__frkG4Result;
    if (!button || !result?.packs?.length || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding G4 packs…';
    setG4Message(root, '');

    try {
      const items = result.packs.map((pack) => ({
        id: Number(pack.variantId),
        quantity: Number(pack.count)
      }));

      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        let message = 'Unable to add the G4 packs. Please try again.';
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      button.textContent = 'G4 packs added ✓';
      setG4Message(root, `${packText(result.packs)} added to your basket.`);

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: true }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { open: true }
      }));
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { open: true }
        }));
        if (window.updateVatPrices) window.updateVatPrices();
      }, 350);

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    } catch (error) {
      setG4Message(root, error?.message || 'Unable to add the G4 packs. Please try again.', true);
      button.textContent = originalText;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function updateG4(root) {
    const size = numberValue(root.dataset.frkRoofSize);
    const required = Math.round(size * 0.5 * 10) / 10;
    const result = cheapestPackBreakdown(required, packOptions(root));
    const requiredTarget = root.querySelector('[data-frk-g4-required]');
    const packsTarget = root.querySelector('[data-frk-g4-packs]');
    const overageTarget = root.querySelector('[data-frk-g4-overage]');
    const totalTarget = root.querySelector('[data-frk-g4-total]');

    if (requiredTarget) requiredTarget.textContent = formatKg(required);

    if (!result) {
      if (packsTarget) packsTarget.textContent = 'Call us for the best pack mix';
      if (overageTarget) overageTarget.hidden = true;
      if (totalTarget) totalTarget.textContent = 'Contact us';
      setG4ButtonState(root, null);
      return;
    }

    setG4ButtonState(root, result);
    if (packsTarget) packsTarget.textContent = packText(result.packs);

    if (overageTarget) {
      if (result.overageKg > 0.001) {
        overageTarget.textContent = `Supplies ${formatKg(result.suppliedKg)}kg · ${formatKg(result.overageKg)}kg spare`;
        overageTarget.hidden = false;
      } else {
        overageTarget.hidden = true;
      }
    }

    if (totalTarget) {
      totalTarget.setAttribute('data-vat-price', '');
      totalTarget.setAttribute('data-price-cents', String(result.totalCents));
      if (window.__VAT && typeof window.__VAT.applyVatToAllPrices === 'function') {
        window.__VAT.applyVatToAllPrices(root);
      } else {
        totalTarget.textContent = fallbackMoney(result.totalCents);
      }
    }
  }

  function substrateSummary(key) {
    const summaries = {
      osb: 'OSB3 decking · Recommended',
      concrete: 'Concrete · G4 primer required',
      asphalt: 'Asphalt · Contact us',
      felt: 'Existing felt · Contact us',
      grp: 'Existing GRP · Contact us',
      pir: 'PIR insulation · Contact us'
    };
    return summaries[key] || 'Roof surface selected';
  }

  function setSelected(root, key, focusPanel = false, userInitiated = false) {
    const options = Array.from(root.querySelectorAll('[data-frk-substrate-option]'));
    const panels = Array.from(root.querySelectorAll('[data-frk-substrate-panel]'));
    const selected = options.find((option) => option.dataset.frkSubstrateOption === key);

    if (!selected) return;

    options.forEach((option) => {
      const active = option === selected;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', active ? 'true' : 'false');
      option.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel) => {
      const active = panel.dataset.frkSubstratePanel === key;
      panel.hidden = !active;
      if (active && focusPanel) panel.setAttribute('tabindex', '-1');
    });

    // G4 damp sealer pack pricing is only needed on the Concrete panel, and
    // OSB (not Concrete) is the default selection - loading it here instead
    // of unconditionally at startup avoids up to ~13 parallel fetches
    // (predictive search suggest + per-pack-size product lookups) firing on
    // every roofing-kit PDP load regardless of which substrate the customer
    // actually wants. loadG4PackOptions is idempotent (guards on
    // root.__frkG4PackPromise), so this is safe to call every time Concrete
    // is (re)selected. setG4LoadingState() runs synchronously first so the
    // "Loading pack options…" state is visible immediately on first select,
    // not just after the fetch resolves.
    if (key === 'concrete' && !root.__frkG4PackPromise) {
      setG4LoadingState(root);
      loadG4PackOptions(root);
    }

    selected.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    updateArrows(root);

    root.dispatchEvent(new CustomEvent('frk:substrate-selected', {
      bubbles: true,
      detail: {
        key,
        summary: substrateSummary(key),
        userInitiated
      }
    }));
  }

  function updateArrows(root) {
    const viewport = root.querySelector('[data-frk-substrate-viewport]');
    const prev = root.querySelector('[data-frk-substrate-prev]');
    const next = root.querySelector('[data-frk-substrate-next]');
    if (!viewport || !prev || !next) return;

    const overflow = viewport.scrollWidth > viewport.clientWidth + 2;
    const atStart = !overflow || viewport.scrollLeft <= 3;
    const atEnd = !overflow || viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 3;

    prev.hidden = !overflow;
    next.hidden = !overflow;
    prev.disabled = atStart;
    next.disabled = atEnd;
    prev.classList.toggle('is-hidden', atStart);
    next.classList.toggle('is-hidden', atEnd);
    prev.setAttribute('aria-hidden', atStart ? 'true' : 'false');
    next.setAttribute('aria-hidden', atEnd ? 'true' : 'false');

    root.classList.toggle('has-left-overflow', overflow && !atStart);
    root.classList.toggle('has-right-overflow', overflow && !atEnd);
  }

  function scrollRail(root, direction) {
    const viewport = root.querySelector('[data-frk-substrate-viewport]');
    const option = root.querySelector('[data-frk-substrate-option]');
    if (!viewport) return;

    const track = root.querySelector('.frk-substrate-selector__track');
    const styles = track ? window.getComputedStyle(track) : null;
    const gap = styles ? Number.parseFloat(styles.columnGap || styles.gap || '0') : 0;
    const cardWidth = option ? option.getBoundingClientRect().width : 96;
    const step = Math.max(190, (cardWidth + gap) * 2);

    viewport.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  function openDialog(root, opener) {
    const dialog = root.querySelector('[data-frk-substrate-osb-dialog]');
    if (!dialog) return;

    dialog.__frkLastOpener = opener || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    document.body.classList.add(BODY_OPEN_CLASS);
    window.requestAnimationFrame(() => {
      dialog.querySelector('[data-frk-substrate-osb-close]')?.focus({ preventScroll: true });
    });
  }

  function closeDialog(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');

    document.body.classList.remove(BODY_OPEN_CLASS);
    const opener = dialog.__frkLastOpener;
    if (opener && typeof opener.focus === 'function') {
      window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.frkSubstrateReady === 'true') return;

    const options = Array.from(root.querySelectorAll('[data-frk-substrate-option]'));
    const viewport = root.querySelector('[data-frk-substrate-viewport]');
    const prev = root.querySelector('[data-frk-substrate-prev]');
    const next = root.querySelector('[data-frk-substrate-next]');
    const dialog = root.querySelector('[data-frk-substrate-osb-dialog]');

    if (!options.length) return;
    root.dataset.frkSubstrateReady = 'true';

    const g4AddButton = root.querySelector('[data-frk-g4-add]');
    if (g4AddButton) {
      g4AddButton.addEventListener('click', () => addG4Packs(root));
    }

    setSelected(root, 'osb');

    options.forEach((option, index) => {
      option.addEventListener('click', () => setSelected(root, option.dataset.frkSubstrateOption, true, true));

      option.addEventListener('keydown', (event) => {
        let targetIndex = null;
        if (event.key === 'ArrowRight') targetIndex = (index + 1) % options.length;
        if (event.key === 'ArrowLeft') targetIndex = (index - 1 + options.length) % options.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = options.length - 1;
        if (targetIndex === null) return;

        event.preventDefault();
        const target = options[targetIndex];
        setSelected(root, target.dataset.frkSubstrateOption, false, true);
        target.focus({ preventScroll: true });
      });
    });

    prev?.addEventListener('click', () => scrollRail(root, -1));
    next?.addEventListener('click', () => scrollRail(root, 1));
    viewport?.addEventListener('scroll', () => window.requestAnimationFrame(() => updateArrows(root)), { passive: true });
    viewport?.addEventListener('scrollend', () => updateArrows(root), { passive: true });

    root.querySelectorAll('[data-frk-substrate-osb-open]').forEach((opener) => {
      opener.addEventListener('click', () => openDialog(root, opener));
    });

    root.querySelectorAll('[data-frk-substrate-osb-close]').forEach((closer) => {
      closer.addEventListener('click', () => closeDialog(dialog));
    });

    if (dialog) {
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });

      dialog.addEventListener('click', (event) => {
        const panel = dialog.querySelector('.frk-substrate-sheet__panel');
        if (!panel) return;
        const rect = panel.getBoundingClientRect();
        const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) closeDialog(dialog);
      });

      dialog.addEventListener('close', () => document.body.classList.remove(BODY_OPEN_CLASS));
    }

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => updateArrows(root))
      : null;
    if (viewport && resizeObserver) {
      resizeObserver.observe(viewport);
      root.__frkSubstrateResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', () => updateArrows(root), { passive: true });
    }

    window.requestAnimationFrame(() => updateArrows(root));
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-frk-substrate-selector]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  window.addEventListener('vat:modeChanged', () => {
    document.querySelectorAll('[data-frk-substrate-selector]').forEach((root) => {
      if (root.__frkG4PackOptions) updateG4(root);
    });
  });
})();
